/**
 * LinkExistingDialog — pulls churches / schools / tenants from their own
 * modules into the Organization Hierarchy.
 *
 * Backend wiring
 *   GET  /api/admin/org/linkable?kind=&search=&include_linked=
 *   POST /api/admin/org/units/link-create
 *
 * UX
 *   Two-pane dialog:
 *     ┌─────────────┬──────────────────────────────────────┐
 *     │ Picker      │ Detail / placement form              │
 *     │ • Kind tabs │ • Suggested parent (auto-fill)       │
 *     │ • Search    │ • Manual parent picker               │
 *     │ • List rows │ • Quick-add ancestors (country/...)  │
 *     │             │ • Confirm                            │
 *     └─────────────┴──────────────────────────────────────┘
 *
 * Already-linked entries appear with a faded badge so operators don't try
 * to link them twice — though they can still click for visibility.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Link2, ChevronRight, Sparkles, Plus } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const h = () => ({ ...authHeaders(), 'Content-Type': 'application/json' });

const KIND_TABS = [
  { value: 'all',    label: 'All' },
  { value: 'school', label: 'Schools', icon: '🏫' },
  { value: 'church', label: 'Churches', icon: '⛪' },
  { value: 'tenant', label: 'Tenants',  icon: '🏢' },
];

const HIERARCHY_LEVELS = [
  'hq', 'province', 'region', 'country', 'district', 'campus', 'assembly', 'home_group',
];

export const LinkExistingDialog = ({ open, onOpenChange, allUnits = [], onLinked }) => {
  const qc = useQueryClient();
  const [kind, setKind]         = useState('all');
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState(null);   // the entity being placed
  const [parentId, setParentId] = useState('');
  const [autoCountry,  setAutoCountry]  = useState('');
  const [autoRegion,   setAutoRegion]   = useState('');
  const [autoDistrict, setAutoDistrict] = useState('');
  const [overrideLevel, setOverrideLevel] = useState('');

  // Reload + reset whenever the dialog opens
  useEffect(() => {
    if (open) {
      setKind('all'); setSearch(''); setSelected(null); setParentId('');
      setAutoCountry(''); setAutoRegion(''); setAutoDistrict(''); setOverrideLevel('');
    }
  }, [open]);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['org-linkable', kind, search],
    queryFn: async () => (await axios.get(
      `${API}/api/admin/org/linkable`,
      { headers: h(), params: { kind: kind === 'all' ? undefined : kind,
                                 search: search || undefined,
                                 include_linked: true } })).data,
    enabled: open,
  });

  // Pre-fill auto-ancestors and parent suggestion when an entity is picked
  useEffect(() => {
    if (!selected) return;
    setAutoCountry(selected.country || '');
    setAutoRegion(''); setAutoDistrict(selected.city || '');
    setParentId(selected.suggested_parent_id || '');
    setOverrideLevel(selected.default_level || '');
  }, [selected]);

  const link = useMutation({
    mutationFn: async () => {
      const body = {
        kind: selected.kind, ext_id: selected.ext_id,
        parent_id: parentId || null,
        level: overrideLevel || undefined,
      };
      const anc = {};
      if (!parentId && autoCountry)  anc.country  = autoCountry;
      if (!parentId && autoRegion)   anc.region   = autoRegion;
      if (!parentId && autoDistrict) anc.district = autoDistrict;
      if (Object.keys(anc).length) body.auto_create_ancestors = anc;
      return (await axios.post(`${API}/api/admin/org/units/link-create`,
        body, { headers: h() })).data;
    },
    onSuccess: (unit) => {
      toast.success(`${selected.icon} ${unit.name} linked into the hierarchy`);
      qc.invalidateQueries({ queryKey: ['org-tree'] });
      qc.invalidateQueries({ queryKey: ['org-linkable'] });
      onLinked?.(unit);
      onOpenChange(false);
    },
    onError: (e) => {
      const d = e?.response?.data?.detail;
      if (typeof d === 'object' && d?.detail) {
        toast.error(`${d.detail} (existing unit: ${d.linked_unit_name || d.linked_unit_id})`);
      } else {
        toast.error(d || 'Linking failed');
      }
    },
  });

  // Parent picker shows existing units sorted by level + name.
  const parentOptions = useMemo(() => {
    const flat = [];
    const walk = (units, depth = 0) => {
      for (const u of units || []) {
        flat.push({ ...u, _depth: depth });
        if (u.children) walk(u.children, depth + 1);
      }
    };
    walk(allUnits);
    return flat;
  }, [allUnits]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-white" data-testid="link-existing-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-indigo-500" />
            Link existing church / school / tenant
          </DialogTitle>
          <DialogDescription className="text-xs">
            Pull entities you&apos;ve already created in other modules onto the Organization tree.
            No data is duplicated — the unit keeps an <code>external_ref</code> pointing back to the source.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[70vh]">
          {/* ----- LEFT: picker ----- */}
          <div className="flex flex-col gap-2 min-h-0">
            <Tabs value={kind} onValueChange={setKind}>
              <TabsList className="w-full">
                {KIND_TABS.map(t => (
                  <TabsTrigger key={t.value} value={t.value} className="flex-1" data-testid={`link-tab-${t.value}`}>
                    {t.icon && <span className="mr-1">{t.icon}</span>}{t.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search by name…" className="pl-8"
                      data-testid="link-search" />
            </div>

            <div className="border rounded overflow-y-auto flex-1 min-h-[280px]">
              {isLoading ? (
                <div className="p-6 text-center text-sm text-slate-500">Loading…</div>
              ) : rows.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-500">No matches.</div>
              ) : rows.map((r) => (
                <button key={`${r.kind}:${r.ext_id}`}
                        type="button"
                        onClick={() => !r.linked && setSelected(r)}
                        disabled={r.linked}
                        className={`w-full text-left px-3 py-2 border-b last:border-0 flex items-center gap-3 transition ${
                          selected?.ext_id === r.ext_id ? 'bg-indigo-50' :
                          r.linked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-50'
                        }`}
                        data-testid={`link-row-${r.kind}-${r.ext_id}`}>
                  <span className="text-xl">{r.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.name}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {[r.country, r.city].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </div>
                  {r.linked ? <Badge variant="outline" className="text-emerald-700 border-emerald-300">linked</Badge>
                            : <ChevronRight className="h-4 w-4 text-slate-400" />}
                </button>
              ))}
            </div>
            <div className="text-xs text-slate-500">
              {rows.length} entit{rows.length === 1 ? 'y' : 'ies'} · already-linked rows greyed
            </div>
          </div>

          {/* ----- RIGHT: placement ----- */}
          <div className="border rounded p-4 bg-slate-50/40 min-h-[280px] overflow-y-auto">
            {!selected ? (
              <div className="text-sm text-slate-500 h-full flex items-center justify-center text-center">
                Pick an entity on the left to choose where on the tree it goes.
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{selected.icon}</span>
                  <div>
                    <div className="font-medium">{selected.name}</div>
                    <div className="text-xs text-slate-500">
                      {selected.label} · {selected.default_unit_type}
                    </div>
                  </div>
                </div>

                {selected.suggested_parent_id && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs flex items-start gap-2"
                          data-testid="link-suggested-parent">
                    <Sparkles className="h-3.5 w-3.5 text-amber-600 mt-0.5" />
                    <div className="flex-1">
                      Auto-suggested parent: <strong>{selected.suggested_parent_name}</strong>
                    </div>
                    <Button size="sm" variant="outline" className="h-6 text-xs"
                              onClick={() => setParentId(selected.suggested_parent_id)}>
                      Use
                    </Button>
                  </div>
                )}

                <div>
                  <Label className="text-xs">Place under existing unit</Label>
                  <Select value={parentId || 'NONE'} onValueChange={(v) => setParentId(v === 'NONE' ? '' : v)}>
                    <SelectTrigger className="mt-1" data-testid="link-parent-select">
                      <SelectValue placeholder="(none — create as root or use quick-add below)" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="NONE">(none — use quick-add below)</SelectItem>
                      {parentOptions.map(u => (
                        <SelectItem key={u.id || u._id} value={u.id || u._id}>
                          {'\u00A0'.repeat(u._depth * 2)}
                          {u.name} <span className="text-slate-400 ml-1">· {u.level}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <details open={!parentId} className="border-t pt-2">
                  <summary className="cursor-pointer text-xs font-medium text-slate-600 flex items-center gap-1">
                    <Plus className="h-3 w-3" /> Quick-add ancestors {parentId && <span className="text-slate-400">(disabled while parent is set)</span>}
                  </summary>
                  <div className="mt-2 space-y-2">
                    <p className="text-[11px] text-slate-500">
                      Missing rungs will be auto-created. Leave blank to skip a level.
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <div>
                        <Label className="text-[11px]">Country</Label>
                        <Input value={autoCountry} onChange={(e) => setAutoCountry(e.target.value)}
                                placeholder="Zimbabwe" disabled={!!parentId}
                                className="h-8 text-xs" data-testid="link-auto-country" />
                      </div>
                      <div>
                        <Label className="text-[11px]">Region</Label>
                        <Input value={autoRegion} onChange={(e) => setAutoRegion(e.target.value)}
                                placeholder="Mashonaland" disabled={!!parentId}
                                className="h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="text-[11px]">District</Label>
                        <Input value={autoDistrict} onChange={(e) => setAutoDistrict(e.target.value)}
                                placeholder="Harare" disabled={!!parentId}
                                className="h-8 text-xs" data-testid="link-auto-district" />
                      </div>
                    </div>
                  </div>
                </details>

                <div className="border-t pt-2">
                  <Label className="text-xs">Mount as level</Label>
                  <Select value={overrideLevel || selected.default_level}
                            onValueChange={setOverrideLevel}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {HIERARCHY_LEVELS.map(l => (
                        <SelectItem key={l} value={l}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Default for {selected.label.toLowerCase()}s: <code>{selected.default_level}</code>.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => link.mutate()}
                    disabled={!selected || link.isPending}
                    data-testid="link-confirm">
            <Link2 className="h-4 w-4 mr-1" />
            {link.isPending ? 'Linking…' : 'Link into hierarchy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkExistingDialog;
